import {
  type SolanaSigner,
  deserializeSolanaTransaction,
  signAndSendSolanaTransaction,
  simulateSolanaTransaction,
} from "@rarible_int/anaconda_sdk/solana"

/**
 * The SDK's own `Connection`: the app and the SDK depend on different
 * web3.js releases, whose classes do not type-check against each other.
 */
type SdkConnection = Parameters<
  typeof signAndSendSolanaTransaction
>[0]["connection"]

/**
 * Signs and sends a transaction an external marketplace built for this
 * wallet, after simulating it so a stale listing or bid fails before the
 * wallet prompt rather than after.
 *
 * The BFF only hands on transactions whose sole signer and fee payer is the
 * user it built them for; checking the connected signer is that user keeps a
 * wallet switch mid-flow from signing someone else's trade.
 */
export async function sendExternalSolanaTransaction({
  connection,
  signer,
  from,
  bytes,
}: {
  connection: SdkConnection
  signer: SolanaSigner
  from: string
  bytes: number[]
}): Promise<string> {
  if (signer.publicKey?.toBase58() !== from) {
    throw new Error("Solana signer does not match the wallet this trade is for")
  }

  const transaction = deserializeSolanaTransaction(Uint8Array.from(bytes))
  try {
    await simulateSolanaTransaction({ connection, transaction })
  } catch (error) {
    throw describeSimulationFailure(error)
  }
  try {
    return await signAndSendSolanaTransaction({
      connection,
      signer,
      transaction,
    })
  } catch (error) {
    throw describeSendFailure(error)
  }
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

/**
 * A failed pre-signing simulation, in words: the SDK's own error carries the
 * raw program logs. Between the BFF building the trade and this check someone
 * else can take the listing or bid, which is by far the usual cause.
 */
export function describeSimulationFailure(error: unknown): unknown {
  const message = messageOf(error)
  if (!/simulation failed/i.test(message)) return error
  // "AccountNotFound" is what a fee payer with no SOL at all gets.
  if (
    /insufficient[ _]?(lamports|funds)|AccountNotFound|InsufficientFundsFor/i.test(
      message,
    )
  ) {
    return new Error("Error: insufficient funds")
  }
  return new Error(
    "This listing or offer changed. Refresh the page and try again.",
  )
}

/**
 * A failed send, in words. The marketplace's blockhash lives about a minute
 * (Orbis builds its own): a wallet prompt left open longer is refused before
 * sending ("Blockhash not found"), while one sent too late expires unconfirmed
 * — and a transaction whose blockhash has expired can no longer land.
 */
export function describeSendFailure(error: unknown): unknown {
  const message = messageOf(error)
  if (
    /not confirmed before its blockhash expired|block height exceeded/i.test(
      message,
    )
  ) {
    return new Error(
      "The transaction was not confirmed in time, so it did not go through. Try again.",
    )
  }
  if (/blockhash not found/i.test(message)) {
    return new Error("The transaction expired before it was signed. Try again.")
  }
  return error
}

/**
 * What to tell the user when the BFF refuses to build an external trade.
 * The BFF's own message wins on a 400 or 409, the statuses whose text it writes
 * for people.
 */
export async function readExternalTradeError(
  response: Response,
  marketplace: string,
): Promise<Error> {
  const body: unknown = await response.json().catch(() => undefined)
  const message =
    typeof body === "object" &&
    body !== null &&
    "error" in body &&
    typeof body.error === "string"
      ? body.error
      : undefined

  if (response.status === 409) {
    return new Error(
      `${message ?? `The ${marketplace} listing changed`}. Refresh the page and try again.`,
    )
  }
  if (response.status === 422) {
    return new Error("Error: insufficient funds")
  }
  if (response.status === 503) {
    return new Error(`${marketplace} is busy right now. Try again in a moment.`)
  }
  // Only a 400 carries a message we wrote for people; a 5xx body can hold an
  // upstream error page or RPC text.
  if (response.status === 400 && message) return new Error(message)
  return new Error(`${marketplace} could not create this trade`)
}
